import React from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import Input from "components/Input/Input";
import Button from "components/Button/Regular";
import Select from "components/Input/Select";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { clearActiveOrg } from "services/active-org.service";
import { deleteOrganisation, readOrganisation, updateOrganisation, OrganisationDetail } from "services/clinics.service";

type FormValues = {
  orgName: string;
  currency: string;
};

const CURRENCY_MAP: Record<string, number> = {
  euro: 978,
  dollar: 840,
  pound: 826,
};

const CURRENCY_REV: Record<number, string> = {
  978: "euro",
  840: "dollar",
  826: "pound",
};

const UpdateClinicForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { orgId: activeOrgId, setActiveOrg, organisations, refreshOrganisations } =
    useActiveOrg();
  const { getAccessTokenSilently } = useAuth0();
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const fullOrgRef = React.useRef<OrganisationDetail | null>(null);
  // Save stays off until the clinic has been read: the update spreads the full
  // record, so submitting without it would blank everything but name/currency.
  const [loaded, setLoaded] = React.useState(false);

  const orgId = id ? Number(id) : NaN;

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: { orgName: "", currency: "euro" },
  });

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      fullOrgRef.current = null;
      setLoaded(false);
      if (!id || !Number.isFinite(orgId) || orgId <= 0) return;
      setLoadError(null);
      setSuccessMsg(null);
      try {
        const token = await getAccessTokenSilently();
        const { data, error } = await readOrganisation(token, orgId);
        if (cancelled) return;
        if (error) {
          setLoadError(error.message);
          return;
        }
        if (!data) return;
        fullOrgRef.current = data;
        const cur =
          data.monetaryCurrency != null
            ? CURRENCY_REV[data.monetaryCurrency] ?? "euro"
            : "euro";
        reset({
          orgName: data.orgName,
          currency: cur,
        });
        setLoaded(true);
      } catch (e) {
        if (!cancelled) setLoadError((e as Error).message);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, orgId, getAccessTokenSilently, reset]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setSubmitError(null);
    setSuccessMsg(null);
    if (!Number.isFinite(orgId) || orgId <= 0) {
      setSubmitError("Missing clinic id.");
      return;
    }
    if (!fullOrgRef.current) {
      setSubmitError("The clinic has not loaded, so it cannot be saved.");
      return;
    }
    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const monetaryCurrency = CURRENCY_MAP[data.currency] ?? 978;
      const { error } = await updateOrganisation(token, {
        ...fullOrgRef.current,
        orgId,
        orgName: data.orgName,
        monetaryCurrency,
      });
      if (error) setSubmitError(error.message);
      else {
        setSuccessMsg("Clinic updated.");
        if (activeOrgId === orgId) {
          setActiveOrg(orgId, data.orgName);
        }
        // Keep the header's clinic switcher in step with the new name.
        await refreshOrganisations();
      }
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Delete this organisation? This removes the clinic from the system for users who are allowed to manage it."
      )
    ) {
      return;
    }
    if (!Number.isFinite(orgId) || orgId <= 0) return;
    setDeleting(true);
    setSubmitError(null);
    try {
      const token = await getAccessTokenSilently();
      const { error } = await deleteOrganisation(token, orgId);
      if (error) {
        setSubmitError(error.message);
        return;
      }
      if (activeOrgId === orgId) {
        const next = organisations.find((o) => o.orgId !== orgId);
        if (next) {
          setActiveOrg(next.orgId, next.orgName);
        } else {
          clearActiveOrg();
        }
      }
      // Drop the deleted clinic from the header switcher; the refresh also
      // re-selects or clears the active org if it is no longer in the list.
      await refreshOrganisations();
      navigate("/app/clinics");
    } catch (e) {
      setSubmitError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="px-5 py-5">
      {loadError && (
        <div className="mb-4 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadError}
        </div>
      )}
      {successMsg && (
        <div className="mb-4 rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          {successMsg}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <Input
          {...register("orgName")}
          label="Organisation name"
          placeholder="Clinic name"
        />

        <Select
          {...register("currency")}
          label="Currency"
          placeholder="Currency"
          options={[
            { title: "Euro", value: "euro" },
            { title: "Dollar", value: "dollar" },
            { title: "Pound", value: "pound" },
          ]}
        />
      </div>

      {submitError && (
        <div className="mb-4 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700">
          {submitError}
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
        <button
          type="button"
          disabled={deleting || submitting}
          onClick={handleDelete}
          className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-vg-cherry hover:bg-vg-cherry/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {deleting ? "Deleting…" : "Delete clinic"}
        </button>
        <Button color="Primary" type="submit" disabled={!loaded || submitting || deleting}>
          <span>{submitting ? "Saving…" : "Save changes"}</span>
        </Button>
      </div>
    </form>
  );
};

export default UpdateClinicForm;
